import ModalBlank from "@shared/components/modals/ModalBlank";
import { Loader2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { pollService } from "../services/poll.service";
import { Poll, PollQuestionVoters, PollVoter } from "../types/poll.types";

interface PollVotersModalProps {
  poll: Poll;
  isOpen: boolean;
  onClose: () => void;
}

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

/** Who picked each option. Only ever opened on a poll that is not anonymous. */
export function PollVotersModal({ poll, isOpen, onClose }: PollVotersModalProps) {
  const modalId = useId();
  const [questions, setQuestions] = useState<PollQuestionVoters[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !poll.id) return;

    let cancelled = false;
    setQuestions(null);
    setError(null);

    pollService
      .getVoters(poll.id)
      .then((result) => {
        if (!cancelled) setQuestions(result);
      })
      .catch((requestError) => {
        console.error("Error loading poll voters:", requestError);
        if (!cancelled) setError("This poll's votes could not be loaded.");
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, poll.id]);

  return (
    <ModalBlank id={modalId} modalOpen={isOpen} setModalOpen={() => onClose()} showCloseButton>
      <div className="p-5 sm:p-6">
        <div className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">Votes</div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        {!questions && !error && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {questions && (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
            {poll.questions.map((question, questionIndex) => {
              const answers = questions.find((entry) => entry.questionId === question.id);

              return (
                <div key={question.id}>
                  <div className="font-medium text-gray-800 dark:text-gray-100 mb-2">
                    {poll.questions.length > 1 && `${questionIndex + 1}. `}
                    {question.text}
                  </div>

                  <div className="space-y-3">
                    {question.options.map((option) => {
                      const voters = answers?.options.find(
                        (entry) => entry.optionId === option.id,
                      )?.voters ?? [];

                      return (
                        <div
                          key={option.id}
                          className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
                        >
                          <div className="flex justify-between items-center text-sm mb-2">
                            <span className="text-gray-800 dark:text-gray-100">{option.text}</span>
                            <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                              {voters.length}
                            </span>
                          </div>

                          {voters.length === 0 ? (
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Nobody picked this one.
                            </p>
                          ) : (
                            <ul className="flex flex-wrap gap-2">
                              {voters.map((voter: PollVoter) => (
                                <li
                                  key={voter.id}
                                  className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-full pl-1 pr-3 py-1"
                                >
                                  {voter.imageUrl ? (
                                    <img
                                      src={voter.imageUrl}
                                      alt=""
                                      className="w-6 h-6 rounded-full object-cover"
                                    />
                                  ) : (
                                    <span className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 text-[10px] font-bold flex items-center justify-center text-gray-700 dark:text-gray-200">
                                      {initials(voter.name)}
                                    </span>
                                  )}
                                  <span className="text-xs text-gray-700 dark:text-gray-200">
                                    {voter.name}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ModalBlank>
  );
}
